import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, seededRandom } from "../spec";
import { average, median, percentileInc, stdevS } from "./estadistica";

const SHEET = "MT Salarios";
const TABLE = "Salarios";
const CIUDADES = ["Monterrey", "CDMX", "Guadalajara", "Querétaro"] as const;

const rnd = seededRandom(8383);
// Puesto, Ciudad, Años de experiencia, Salario mensual
const ROWS = Array.from({ length: 36 }, (_, i) => {
  // Three directors pull the average up: the median does not move.
  const director = i === 7 || i === 21 || i === 30;
  const exp = i % 5 === 0 ? 3 : rnd.int(0, 15);
  const salario = director ? rnd.int(95, 140) * 1000 : 16000 + exp * 1200 + rnd.int(0, 40) * 250;
  return [
    director ? "Director comercial" : rnd.pick(["Analista de datos", "Analista financiero", "Contador", "Ejecutivo de ventas"]),
    rnd.pick(CIUDADES),
    exp,
    salario,
  ] as [string, string, number, number];
});

type Row = (typeof ROWS)[number];
const salarios = ROWS.map((r: Row) => r[3]);
const q1 = percentileInc(salarios, 0.25);
const q3 = percentileInc(salarios, 0.75);
const iqr = q3 - q1;
const IN = { ciudad: "Monterrey" };

/** MODA.UNO: most frequent value; ties go to the one that appears first. */
const moda = (values: number[]) => {
  let best = values[0];
  let bestCount = 0;
  for (const v of values) {
    const c = values.filter((x) => x === v).length;
    if (c > bestCount) [best, bestCount] = [v, c];
  }
  return best;
};

export const spec: FuncionProjectSpec = {
  projectId: "funciones-estadistica",
  sheets: [
    {
      name: SHEET,
      title: "Salarios de mercado",
      subtitle: "Encuesta salarial — resume los datos sin dejarte engañar por los valores extremos",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Puesto", "Ciudad", "Experiencia", "Salario"],
          rows: ROWS,
          answerColumns: ["Atípico"],
          formats: { Salario: "$#,##0" },
        },
      ],
      inputs: [{ address: "I4", label: "Ciudad", value: IN.ciudad }],
      answers: [
        { address: "I7", label: "Tarea 1 · Promedio", format: "$#,##0" },
        { address: "I8", label: "Tarea 2 · Mediana", format: "$#,##0" },
        { address: "I9", label: "Tarea 3 · Experiencia más común" },
        { address: "I10", label: "Tarea 4 · Desviación estándar", format: "$#,##0" },
        { address: "I11", label: "Tarea 5 · Percentil 90", format: "$#,##0" },
        { address: "I12", label: "Tarea 6 · Rango intercuartil", format: "$#,##0" },
        { address: "I14", label: "Tarea 8 · Mediana de la ciudad", format: "$#,##0" },
      ],
    },
  ],
  checks: {
    1: { kind: "cell", sheet: SHEET, address: "I7", fns: ["AVERAGE"], expected: average(salarios) },
    2: { kind: "cell", sheet: SHEET, address: "I8", fns: ["MEDIAN"], expected: median(salarios) },
    3: { kind: "cell", sheet: SHEET, address: "I9", fns: ["MODE.SNGL"], expected: moda(ROWS.map((r) => r[2])) },
    4: { kind: "cell", sheet: SHEET, address: "I10", fns: ["STDEV.S"], expected: stdevS(salarios) },
    5: { kind: "cell", sheet: SHEET, address: "I11", fns: ["PERCENTILE.INC"], expected: percentileInc(salarios, 0.9) },
    6: {
      kind: "cell", sheet: SHEET, address: "I12", fns: ["QUARTILE.INC"], minCounts: { "QUARTILE.INC": 2 },
      expected: iqr,
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Atípico", fns: ["IF", "QUARTILE.INC"], exact: true,
      expected: ROWS.map((r) => (r[3] > q3 + (3 * iqr) / 2 ? "Sí" : "No")),
    },
    8: {
      kind: "cell", sheet: SHEET, address: "I14", fns: ["MEDIAN", "FILTER"],
      expected: median(ROWS.filter((r) => r[1] === IN.ciudad).map((r) => r[3])),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-estadistica",
  title: "Estadística descriptiva: Salarios de mercado",
  subtitle: "Promedio, mediana, dispersión y valores atípicos",
  description:
    "Recursos Humanos levantó una encuesta salarial de 36 puestos para definir su tabulador. Entre los datos hay tres directores con sueldos muy altos que \"inflan\" el promedio.\n\nAprenderás a resumir datos como un analista: cuándo usar la mediana en lugar del promedio, cuánto varían los datos, qué es un percentil y cómo detectar valores atípicos con la regla del rango intercuartil.",
  topics: [
    "PROMEDIO frente a MEDIANA",
    "MODA.UNO",
    "DESVEST.M: dispersión",
    "PERCENTIL.INC y CUARTIL.INC",
    "Valores atípicos: Q3 + 1.5 × RIC",
    "Mediana con condición: MEDIANA(FILTRAR(...))",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Salario promedio",
      description: "En la hoja <strong>MT Salarios</strong>, celda <strong>I7</strong>: el <strong>promedio</strong> de la columna Salario.",
      solution: [`Selecciona ${F("I7")}.`, `Escribe ${F("=PROMEDIO(Salarios[Salario])")}.`],
      expectedResult: "Promedio (inflado por los directores)",
      finalFormula: "=PROMEDIO(Salarios[Salario])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Salario mediano",
      description:
        "En <strong>I8</strong>: la <strong>mediana</strong> del Salario: el valor que queda justo a la mitad cuando los ordenas. Compárala con el promedio.",
      solution: [
        `Selecciona ${F("I8")}.`,
        `Escribe ${F("=MEDIANA(Salarios[Salario])")}.`,
        "La mediana es más baja que el promedio: tres sueldos muy altos jalan el promedio hacia arriba, pero casi no mueven la mediana. Para salarios, precios de casas o ingresos, la mediana describe mejor al \"empleado típico\".",
      ],
      expectedResult: "Menor que el promedio",
      finalFormula: "=MEDIANA(Salarios[Salario])",
    },
    {
      id: 3,
      level: "basico",
      title: "Tarea 3: Experiencia más común",
      description: "En <strong>I9</strong>: los años de <strong>Experiencia</strong> que más se repiten.",
      solution: [`Selecciona ${F("I9")}.`, `Escribe ${F("=MODA.UNO(Salarios[Experiencia])")}.`],
      expectedResult: "3",
      finalFormula: "=MODA.UNO(Salarios[Experiencia])",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: ¿Qué tanto varían los salarios?",
      description:
        "En <strong>I10</strong>: la <strong>desviación estándar</strong> del Salario. Es una <strong>muestra</strong> del mercado, no todos los empleados del país.",
      solution: [
        `Selecciona ${F("I10")}.`,
        "DESVEST.M es para muestras (lo más común en análisis); DESVEST.P solo si tienes a toda la población.",
        `Escribe ${F("=DESVEST.M(Salarios[Salario])")}.`,
      ],
      expectedResult: "Dispersión en pesos",
      finalFormula: "=DESVEST.M(Salarios[Salario])",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: Percentil 90",
      description: "En <strong>I11</strong>: el salario del <strong>percentil 90</strong>: el 90% de los salarios está por debajo de ese valor.",
      solution: [
        `Selecciona ${F("I11")}.`,
        `Escribe el percentil como porcentaje (${F("90%")}): funciona igual con punto o coma decimal.`,
        `Escribe ${F("=PERCENTIL.INC(Salarios[Salario],90%)")}.`,
      ],
      expectedResult: "Salario del 10% mejor pagado",
      finalFormula: "=PERCENTIL.INC(Salarios[Salario],90%)",
    },
    {
      id: 6,
      level: "intermedio",
      title: "Tarea 6: Rango intercuartil",
      description: conditions("En <strong>I12</strong>: el <strong>rango intercuartil</strong> (RIC) del Salario, que mide la dispersión del 50% central:", [
        "Cuartil 3 (75%) <strong>menos</strong> cuartil 1 (25%)",
      ]),
      solution: [
        `Selecciona ${F("I12")}.`,
        `${F("CUARTIL.INC(rango,3)")} es lo mismo que el percentil 75 y ${F("CUARTIL.INC(rango,1)")} el percentil 25.`,
        `Escribe ${F("=CUARTIL.INC(Salarios[Salario],3)-CUARTIL.INC(Salarios[Salario],1)")}.`,
      ],
      expectedResult: "Diferencia Q3 − Q1",
      finalFormula: "=CUARTIL.INC(Salarios[Salario],3)-CUARTIL.INC(Salarios[Salario],1)",
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Detectar valores atípicos",
      description: conditions(
        "En la columna <strong>Atípico</strong>: marca con <strong>Sí</strong> los salarios anormalmente altos según la regla de Tukey; los demás, <strong>No</strong>:",
        [
          "Límite = <strong>Q3 + 1.5 × RIC</strong> (el RIC está en <strong>I12</strong>)",
          "Atípico si el salario es <strong>mayor</strong> que el límite",
        ]
      ),
      solution: [
        `Selecciona ${F("E5")}.`,
        `Escribe 1.5 como ${F("3/2")}: así la fórmula funciona igual con punto o con coma decimal.`,
        `Escribe ${F('=SI([@Salario]>CUARTIL.INC([Salario],3)+3/2*$I$12,"Sí","No")')}.`,
        "Deberían salir marcados los directores comerciales.",
      ],
      expectedResult: "Sí solo para los sueldos extremos",
      finalFormula: '=SI([@Salario]>CUARTIL.INC([Salario],3)+3/2*$I$12,"Sí","No")',
    },
    {
      id: 8,
      level: "reto",
      title: "Tarea 8: Mediana de una ciudad",
      description:
        "En <strong>I14</strong>: la mediana del Salario <strong>solo</strong> de la ciudad de <strong>I4</strong>. No existe MEDIANA.SI: combina MEDIANA con FILTRAR.",
      solution: [
        `Selecciona ${F("I14")}.`,
        "FILTRAR deja solo los salarios de esa ciudad y MEDIANA los resume.",
        `Escribe ${F("=MEDIANA(FILTRAR(Salarios[Salario],Salarios[Ciudad]=I4))")}.`,
      ],
      expectedResult: "Mediana de Monterrey",
      finalFormula: "=MEDIANA(FILTRAR(Salarios[Salario],Salarios[Ciudad]=I4))",
    },
  ],
};
